import "./styles.css";
import { BRAND, PHOTO_STEPS, SHARPNESS_MIN_VARIANCE, LOW_LIGHT_MIN_LUMA, type PhotoSlotId } from "./config";
import { getLang, setLang, t, LANGS, type Lang } from "./i18n";
import { state, getSlot, allSlotsReady, type SlotState } from "./state";
import { prepareImage, type PreparedImage } from "./lib/imagePrep";
import { laplacianVariance, averageLuma } from "./lib/imageQuality";
import { uploadAllSlots, type UploadProgress, type UploadProgressStatus } from "./upload";
import {
  startLiveCapture,
  type LiveCaptureErrorKind,
  type LiveCaptureHandle,
  type LiveFrameEvaluation,
} from "./lib/liveCapture";

// ADIM 1 (2026-09-04): kamera/analiz/teaser/rapor akışı bu iskeletten
// kaldırıldı. ADIM 2 (2026-09-04): 3-slot ağız içi fotoğraf yükleme akışı
// (consent → slots) bu dosyanın üzerine inşa edildi. Korunanlar: marka
// renkleri, topbar/legal/dil değiştirme mantığı ve parent'a (nexwell-web)
// postMessage köprüsü.

// --- Marka renklerini CSS değişkenlerine yansıt (config tek kaynak) ---
const rootStyle = document.documentElement.style;
rootStyle.setProperty("--primary", BRAND.primary);
rootStyle.setProperty("--primary-dark", BRAND.primaryDark);
rootStyle.setProperty("--accent", BRAND.accent);
rootStyle.setProperty("--bg", BRAND.bg);
rootStyle.setProperty("--text", BRAND.text);
rootStyle.setProperty("--muted", BRAND.muted);
rootStyle.setProperty("--danger", BRAND.danger);
rootStyle.setProperty("--success", BRAND.success);

const app = document.getElementById("app")!;

function topbar(): string {
  const cur = getLang();
  const opts = LANGS.map(
    (l) => `<option value="${l.code}"${l.code === cur ? " selected" : ""}>${l.name}</option>`,
  ).join("");
  return `
    <div class="topbar">
      <h1>Nexwell · ${t("app_title")}</h1>
      <select class="lang-toggle" id="langSel" aria-label="Language / Dil">${opts}</select>
    </div>`;
}

function legal(): string {
  return `<div class="legal">${t("legal_disclaimer")}</div>`;
}

/**
 * Parent sayfaya (nexwell-web) origin-güvenli bir postMessage yollar. Parent'taki
 * FaceLeadPixelBridge bunları yakalayıp Meta Pixel + GA4 funnel event'ine çevirir.
 * Origin '*' bırakılmaz: iframe'i embed eden sayfanın origin'i document.referrer'dan
 * türetilir; türetilemezse bilinen thenexwell origin'lerine hedeflenir.
 * `extra`: adıma özel ek alanlar (ör. step numarası, hata nedeni) — `type` ve
 * `tool` alanları mevcut sözleşmeyi bozmadan sabit kalır (parent bu turda
 * DEĞİŞMİYOR, yalnızca bilinen `type` değerlerine tepki veriyor — bkz.
 * nexwell-web FaceLeadPixelBridge.tsx FUNNEL_EVENTS; bilinmeyen `type`'lar
 * sessizce yok sayılır, bu yüzden yeni event isimleri eklemek güvenli).
 */
function postToParent(type: string, extra: Record<string, unknown> = {}): void {
  if (typeof window === "undefined" || window.parent === window) return;

  const targets: string[] = [];
  try {
    if (document.referrer) targets.push(new URL(document.referrer).origin);
  } catch {
    /* referrer parse edilemezse fallback listesi kullanılır */
  }
  // Güvenli fallback: bilinen parent origin'leri (referrer boş/yetersiz ise).
  for (const o of ["https://www.thenexwell.com", "https://thenexwell.com"]) {
    if (!targets.includes(o)) targets.push(o);
  }

  const message = { type, tool: "intraoral-lead-tool", lang: getLang(), ...extra };
  for (const origin of targets) {
    try {
      window.parent.postMessage(message, origin);
    } catch {
      /* origin uyuşmazsa tarayıcı sessizce yutar; sonraki hedefi dene */
    }
  }
}

/** Bir funnel adımını parent'a yalnızca bir kez bildirir (yeniden render'da tekrar atmaz). */
const parentSignalsSent = new Set<string>();
function notifyParentOnce(type: string, extra?: Record<string, unknown>): void {
  if (parentSignalsSent.has(type)) return;
  parentSignalsSent.add(type);
  postToParent(type, extra);
}

function wireLang() {
  const sel = document.getElementById("langSel") as HTMLSelectElement | null;
  if (!sel) return;
  sel.onchange = () => {
    setLang(sel.value as Lang);
    render();
  };
}

// --- Ekran akışı: consent -> slots -> (ADIM 3/4'ün işi) ---
type Screen = "consent" | "slots" | "uploading" | "done";
let screen: Screen = "consent";

/** Bir slot art arda kaç kez reddedildi (bulanık/karanlık). Kaçış yolu bir kez
 * sunulur; ikinci reddedişte otomatik olarak forcedOk:true işaretlenir — sert
 * kilit yaratmamak için (bkz. plan §3 "Kalite kapısı"). */
const rejectionCount = new Map<PhotoSlotId, number>();

let liveSlot: PhotoSlotId | null = null;
let liveHandle: LiveCaptureHandle | null = null;
let liveEval: LiveFrameEvaluation | null = null;
let liveError: LiveCaptureErrorKind | null = null;
let liveErrorSlot: PhotoSlotId | null = null;
let liveSessionId = 0; // artan oturum sayacı — önceki boolean iptal bayrağının yerine geçer
let liveStreamAttached = false;
let liveFacingMode: "user" | "environment" | null = null;

function consentScreen(): string {
  return `
    <div class="screen center">
      <div class="card">
        <h2>${t("intraoral_consent_title")}</h2>
        <p>${t("intraoral_consent_body")}</p>
        <label class="consent">
          <input type="checkbox" id="consentCheck" />
          <span>${t("intraoral_consent_checkbox")}</span>
        </label>
        <button class="btn" id="consentContinueBtn" disabled style="margin-top:14px">
          ${t("intraoral_consent_continue")}
        </button>
      </div>
    </div>`;
}

function wireConsentScreen() {
  const check = document.getElementById("consentCheck") as HTMLInputElement | null;
  const btn = document.getElementById("consentContinueBtn") as HTMLButtonElement | null;
  if (!check || !btn) return;
  check.onchange = () => {
    btn.disabled = !check.checked;
  };
  btn.onclick = () => {
    if (!check.checked) return;
    state.consentGiven = true;
    notifyParentOnce("nexwell:intraoral-consent-given");
    screen = "slots";
    render();
  };
}

function slotStatusLabel(s: SlotState): string {
  if (s.status === "checking") return "";
  // forcedOk ÖNCE kontrol edilir: status hâlâ "blurry"/"dark" olsa bile
  // (bilerek değiştirmiyoruz — asıl sorun kaydı kalsın), kullanıcıya artık
  // net bir "reddedildi" mesajı DEĞİL, nötr bir "bu haliyle devam ediliyor"
  // mesajı gösterilir (revizyoncu P1, 2026-09-04: önceki tur burada kırmızı
  // "Bulanık" yazısı Continue aktifken de ekranda kalıyordu).
  if (s.forcedOk) return t("slot_status_forced");
  if (s.status === "unreadable") return t("slot_status_unreadable");
  if (s.status === "blurry") return t("slot_status_blurry");
  if (s.status === "dark") return t("slot_status_dark");
  if (s.status === "ok") return t("slot_status_ok");
  return "";
}

/** Oklüzal slotlara özgü, göze çarpan ipucu kutusu — önceden yönerge
 * paragrafının içine gömülüydü, fark edilmiyordu (Onur'un gerçek cihaz
 * testi bulgusu: "telefonu ters çevir uyarısını dikkat çeken bir forma
 * sokmak gerek"). Hem canlı-öncesi kartta hem canlı görünümde gösterilir. */
function occlusalTips(stepId: PhotoSlotId): string {
  if (stepId !== "upper_occlusal" && stepId !== "lower_occlusal") return "";
  const tips = [t("live_tip_flip_phone")];
  if (stepId === "lower_occlusal") tips.push(t("live_tip_more_light"));
  return `<div class="live-tip-box">${tips.map((tip) => `<p>💡 ${tip}</p>`).join("")}</div>`;
}

function slotCard(step: (typeof PHOTO_STEPS)[number], index: number): string {
  const s = getSlot(step.id);
  let statusLabel = slotStatusLabel(s);
  let statusClass = s.forcedOk
    ? "warn"
    : s.status === "blurry" || s.status === "dark" || s.status === "unreadable"
      ? "err"
      : s.status === "ok"
        ? "ok"
        : "";
  const liveErrorKey =
    liveError === null
      ? null
      : liveError === "permission_denied"
        ? "live_error_permission"
        : liveError === "no_camera"
          ? "live_error_no_camera"
          : liveError === "model_unavailable"
            ? "live_error_model"
            : "live_error_no_camera";
  if (step.id === liveErrorSlot && liveErrorKey) {
    statusLabel = t(liveErrorKey);
    statusClass = "err";
  }
  const showUseAnyway =
    (s.status === "blurry" || s.status === "dark") && !s.forcedOk && (rejectionCount.get(step.id) ?? 0) >= 1;

  // Bu slot canlı modda: kamera görünümü TAM EKRAN yerine kartın önizleme
  // kutusuna gömülü gösterilir — sayfanın geri kalanı (diğer kartlar, dots)
  // görünür kalır (Onur'un gerçek cihaz testinde bulduğu geri bildirim; emniyet
  // (b) render()'da .screen'i inert yaptığı için diğer kartlar görünür ama
  // etkileşimsiz kalır). Adım yönergesi (`<p>${t(step.hintKey)}</p>`, yukarıda)
  // zaten kalıcı gösterildiği için ayrıca tekrarlanmıyor.
  if (step.id === liveSlot) {
    const hintText = liveEval ? t(`live_hint_${liveEval.reason}`) : t("live_loading");
    // Ön kamerada ayna efekti (selfie UX) — yalnız EKRANDA, çekilen/analiz
    // edilen kareyi ETKİLEMEZ: canvas her zaman video akışının ham pikselini
    // çizer, CSS transform yalnız görüntülemeyi etkiler.
    const mirrorClass = liveFacingMode === "user" ? " live-video-mirrored" : "";
    return `
      <div class="card slot-card" data-slot="${step.id}">
        <h2>${index + 1}. ${t(step.titleKey)}</h2>
        <p>${t(step.hintKey)}</p>
        ${occlusalTips(step.id)}
        <video id="liveVideo" class="slot-preview live-video${mirrorClass}" playsinline muted autoplay></video>
        <p id="liveHint" class="live-hint">${hintText}</p>
        <p id="liveDebug" class="live-debug"></p>
        <p class="live-privacy-note">${t("live_privacy_note")}</p>
        <div class="live-card-actions">
          <button class="btn secondary" id="liveCancelBtn">${t("live_cancel")}</button>
          <button class="btn secondary" id="liveFlipCameraBtn">${t("live_switch_camera")}</button>
          <button class="btn secondary" id="liveSwitchToFileBtn">${t("live_switch_to_file")}</button>
        </div>
      </div>`;
  }

  return `
    <div class="card slot-card" data-slot="${step.id}">
      <h2>${index + 1}. ${t(step.titleKey)}</h2>
      <p>${t(step.hintKey)}</p>
      ${occlusalTips(step.id)}
      ${
        s.previewUrl
          ? `<img class="slot-preview" src="${s.previewUrl}" alt="" />`
          : `<img class="slot-example" src="${step.exampleImage}"
               data-fallback="1" alt="${t(step.titleKey)}" />`
      }
      ${statusLabel ? `<p class="slot-status ${statusClass}">${statusLabel}</p>` : ""}
      <input type="file" accept="image/*"
        class="slot-input" id="input-${step.id}" style="display:none" />
      <button class="btn secondary slot-pick-btn" data-slot="${step.id}">
        ${s.previewUrl ? t("slot_retake") : t("slot_choose_photo")}
      </button>
      <button class="btn slot-live-btn" data-slot="${step.id}">${t("live_start")}</button>
      ${showUseAnyway ? `<button class="btn slot-use-anyway-btn" data-slot="${step.id}">${t("slot_use_anyway")}</button>` : ""}
    </div>`;
}

function wireLiveOverlay(): void {
  const cancelBtn = document.getElementById("liveCancelBtn") as HTMLButtonElement | null;
  if (cancelBtn) cancelBtn.onclick = () => closeLive();

  const flipBtn = document.getElementById("liveFlipCameraBtn") as HTMLButtonElement | null;
  if (flipBtn) {
    flipBtn.onclick = () => {
      if (!liveSlot) return;
      const id = liveSlot;
      const next = liveFacingMode === "user" ? "environment" : "user";
      startLive(id, next);
    };
  }

  const switchBtn = document.getElementById("liveSwitchToFileBtn") as HTMLButtonElement | null;
  if (switchBtn) {
    switchBtn.onclick = () => {
      const slotId = liveSlot;
      closeLive();
      if (!slotId) return;
      const input = document.getElementById(`input-${slotId}`) as HTMLInputElement | null;
      input?.click();
    };
  }
}

function updateLiveHint(): void {
  const hintEl = document.getElementById("liveHint");
  if (hintEl) hintEl.textContent = liveEval ? t(`live_hint_${liveEval.reason}`) : t("live_loading");

  // GEÇİCİ hata ayıklama satırı (2026-09-05, Onur'un gerçek cihaz testi) —
  // "otomatik çekmiyor" şikayetinin kod hatası mı yoksa model isabetinin
  // (bilinen sınırlama: az eğitim verisi) düşük olması mı olduğunu ayırt
  // etmek için ham sayıları ekrana yazar. K3 ölçümü bitince kaldırılacak.
  const debugEl = document.getElementById("liveDebug");
  if (debugEl && liveEval) {
    // Uygulamanın gösterildiği dilden bağımsız, sabit İngilizce etiketler —
    // bu satır geçici ve yalnız teşhis amaçlı, ayrıca çevrilmiyor.
    debugEl.textContent = `${liveEval.className} (${Math.round(liveEval.confidence * 100)}%) · sharp=${Math.round(liveEval.sharpness)} · light=${Math.round(liveEval.luma)} · streak=${liveEval.readyStreak}`;
  }

  // Metin okumaya gerek kalmadan anlaşılır görsel sinyal: kamera kutusunun
  // çerçeve rengi ready durumunda YEŞİLE döner (Onur'un gerçek cihaz testi
  // bulgusu: "ön kamerayla çekim yaparken kim o yazıları okuyabilir ki").
  const videoEl = document.getElementById("liveVideo");
  if (videoEl) {
    videoEl.classList.toggle("live-video-ready", liveEval?.reason === "ready");
  }
}

function stepsDots(): string {
  const isDone = (s: SlotState) => s.blob !== null && (s.status === "ok" || s.forcedOk);
  const doneCount = state.slots.filter(isDone).length;
  return `
    <div class="steps">
      ${state.slots
        .map((s, i) => {
          const done = isDone(s);
          const active = !done && i === state.slots.findIndex((x) => !isDone(x));
          return `<div class="dot${done ? " done" : ""}${active ? " active" : ""}"></div>`;
        })
        .join("")}
    </div>
    <p class="step-caption">${t("slots_progress")} ${doneCount}/${PHOTO_STEPS.length}</p>`;
}

function slotsScreen(): string {
  return `
    <div class="screen">
      ${stepsDots()}
      ${PHOTO_STEPS.map((step, i) => slotCard(step, i)).join("")}
      <button class="btn" id="slotsContinueBtn" ${allSlotsReady() ? "" : "disabled"}>
        ${t("slots_continue")}
      </button>
    </div>`;
}

/**
 * Kalite-kapısı + state-yazma bloğu. `fromLive: true` ise (canlı yakalamadan
 * gelen kare) kapılar `evaluateFrame` içinde zaten geçildi (sınıf+güven+
 * netlik+ışık) — burada tekrar kontrol etmeye gerek yok, doğrudan "ok" kabul
 * edilir.
 */
function applyPreparedImage(
  id: PhotoSlotId,
  prepared: PreparedImage,
  sharpness: number,
  luma: number,
  opts: { fromLive: boolean },
): void {
  const slot = getSlot(id);
  // Önceki preview varsa belleği serbest bırak.
  if (slot.previewUrl) URL.revokeObjectURL(slot.previewUrl);

  // Yüklenen artefakt BUDUR — EXIF-orientation düzeltilmiş, downscale edilmiş,
  // EXIF/GPS'siz yeniden kodlanmış blob. Orijinal `file` BİLEREK tutulmuyor
  // (bkz. state.ts SlotState.blob yorumu — revizyoncu P0, 2026-09-04).
  slot.blob = prepared.blob;
  slot.previewUrl = prepared.previewUrl;
  slot.sharpness = sharpness;
  slot.luma = luma;

  if (opts.fromLive) {
    slot.status = "ok";
    slot.forcedOk = false;
    rejectionCount.set(id, 0);
    return;
  }

  slot.forcedOk = false;
  if (sharpness < SHARPNESS_MIN_VARIANCE) {
    slot.status = "blurry";
    rejectionCount.set(id, (rejectionCount.get(id) ?? 0) + 1);
  } else if (luma < LOW_LIGHT_MIN_LUMA) {
    slot.status = "dark";
    rejectionCount.set(id, (rejectionCount.get(id) ?? 0) + 1);
  } else {
    slot.status = "ok";
    rejectionCount.set(id, 0);
  }

  // İkinci ret: kaçış yolunu bir kez sunduk, artık sert kilit yaratmadan kabul et.
  if (slot.status !== "ok" && (rejectionCount.get(id) ?? 0) >= 2) {
    slot.forcedOk = true;
  }
}

async function handleFileForSlot(id: PhotoSlotId, file: File) {
  if (id === liveErrorSlot) {
    liveError = null;
    liveErrorSlot = null;
  }
  const slot = getSlot(id);
  slot.status = "checking";
  render();

  try {
    const prepared = await prepareImage(file);
    const sharpness = laplacianVariance(prepared.canvas);
    const luma = averageLuma(prepared.canvas);
    applyPreparedImage(id, prepared, sharpness, luma, { fromLive: false });
  } catch {
    // Dosya decode edilemedi (HEIC bazı tarayıcılarda, bozuk dosya, vb.) —
    // bu bir kalite reddi DEĞİL, okunamaz bir dosya. Kalite kapısıyla aynı
    // rejectionCount/forcedOk mekanizmasına sokulur (revizyoncu P0,
    // 2026-09-04: önceki tur burada rejectionCount hiç artmıyordu, kullanıcı
    // "yine de kullan" butonu hiç görünmeden kalıcı kilitleniyordu).
    slot.status = "unreadable";
    slot.blob = null;
    // forcedOk kasıtlı olarak FALSE'a çekiliyor (revizyoncu P2, 2026-09-04,
    // 2. tur): önceki tur bunu hiç dokunmuyordu — kullanıcı bu slotta daha
    // önce "yine de kullan" ile forcedOk=true yapmışsa (2 kez bulanık/karanlık
    // ret sonrası), sonra "tekrar çek" deyip okunamayan bir dosya seçerse,
    // eski forcedOk=true DEĞERİ kalıyor ve ekranda "bu haliyle devam ediliyor"
    // (nötr/yeşilimsi) etiketi + eski önizleme görünmeye devam ediyordu —
    // oysa artık blob yok, yüklenecek hiçbir şey kalmadı. blob yok, "yine de
    // kullan"ın yükleyecek bir şeyi olmaz. Kullanıcı "Dosya okunamadı"
    // mesajıyla farklı bir dosya seçmeye yönlendirilir; eski önizleme de
    // temizlenir ki yanlışlıkla "kabul edilmiş" görünmesin.
    slot.forcedOk = false;
    if (slot.previewUrl) {
      URL.revokeObjectURL(slot.previewUrl);
      slot.previewUrl = null;
    }
    // "Fotoğraf seç / çek" butonu her zaman aktif kalır, akış kilitlenmez —
    // yalnızca bu slot "hazır" sayılmaz (bkz. allSlotsReady: blob !== null şartı).
  }

  render();
}

/** Aktif canlı yakalama oturumunu kapatır ve akışı bilinen-iyi duruma döndürür.
 * `keepError: true` verilirse (hata sonrası dosya-seçiciye düşerken) hata
 * etiketi ekranda kalır — kullanıcı neden dosya seçiciye yönlendirildiğini
 * görebilsin diye. */
function closeLive(opts?: { keepError: boolean }): void {
  liveHandle?.stop();
  liveSessionId += 1;
  liveSlot = null;
  liveHandle = null;
  liveEval = null;
  liveStreamAttached = false;
  liveFacingMode = null;
  if (!opts?.keepError) {
    liveError = null;
    liveErrorSlot = null;
  }
  render();
}

/** Canlı yakalama oturumu başlatır. `.slot-live-btn` tıklamasından VE
 * kamera-değiştir butonundan çağrılır — ikincisinde aynı slot için
 * yalnızca `facingMode` zorlanmış olarak yeni bir oturum açılır (eskisi
 * liveSessionId sayacıyla kendini kapatır, bkz. iptal yarışı sözleşmesi). */
function startLive(id: PhotoSlotId, facingMode?: "user" | "environment"): void {
  if (getSlot(id).status === "checking") return;
  liveSlot = id;
  liveError = null;
  liveEval = null;
  liveFacingMode = facingMode ?? PHOTO_STEPS.find((s) => s.id === id)?.capture ?? "environment";
  // Kamera değiştirme butonuyla AYNI slot için yeniden çağrıldığında
  // (facingMode verilmiş çağrı) önceki oturumdan `true` kalmış olabilir —
  // sıfırlanmazsa render()'ın emniyet (c) kontrolü "akış koptu" sanıp yeni
  // oturumu video henüz bağlanmadan kapatır (gerçek bug — Onur'un testinde
  // "switch cam'e basınca kameradan çıkıyor" olarak gözlendi).
  liveStreamAttached = false;
  liveSessionId += 1;
  const mySessionId = liveSessionId;
  render();
  postToParent("nexwell:intraoral-live-started", { stepId: id, reason: "live_start" });

  void startLiveCapture({
    slotId: id,
    video: document.getElementById("liveVideo") as HTMLVideoElement,
    facingMode: liveFacingMode,
    isCancelled: () => liveSessionId !== mySessionId,
    callbacks: {
      onStreamReady() {
        if (liveSessionId !== mySessionId) return; // bayat oturum — aktif oturuma karışma
        liveStreamAttached = true;
        // render() ÇAĞIRILMAZ — video elementini yok eder, bkz. plan notu.
      },
      onEvaluation(evaluation) {
        if (liveSessionId !== mySessionId) return;
        liveEval = evaluation;
        updateLiveHint();
      },
      onCapture(prepared, evaluation) {
        if (liveSessionId !== mySessionId) return;
        applyPreparedImage(id, prepared, evaluation.sharpness, evaluation.luma, { fromLive: true });
        postToParent("nexwell:intraoral-live-captured", { stepId: id, reason: "auto" });
        closeLive();
      },
      onError(error) {
        // Bayat bir oturumun hatası aktif (yeni başlatılmış) bir oturumu
        // kapatmasın — TASK-REVIEW bulgusu: iptal+yeniden başlat sonrası
        // eski oturumun geç gelen reddi, yeni oturumu yanlışlıkla öldürüp
        // yanlış bir analytics eventi gönderiyordu.
        if (liveSessionId !== mySessionId) return;
        liveError = error.kind;
        liveErrorSlot = id;
        postToParent("nexwell:intraoral-live-fallback", { stepId: id, reason: error.kind });
        closeLive({ keepError: true });
        const input = document.getElementById(`input-${id}`) as HTMLInputElement | null;
        input?.click();
      },
    },
  }).then((handle) => {
    if (liveSessionId !== mySessionId) {
      handle.stop(); // bu arada başka bir oturum başlamış/iptal edilmiş — sahipsiz handle'ı hemen kapat
      return;
    }
    liveHandle = handle;
  });
}

function wireSlotsScreen() {
  for (const step of PHOTO_STEPS) {
    const input = document.getElementById(`input-${step.id}`) as HTMLInputElement | null;
    const pickBtn = document.querySelector(`.slot-pick-btn[data-slot="${step.id}"]`) as HTMLButtonElement | null;
    if (input && pickBtn) {
      pickBtn.onclick = () => input.click();
      input.onchange = () => {
        const file = input.files?.[0];
        if (file) void handleFileForSlot(step.id, file);
        input.value = "";
      };
    }
  }

  for (const btn of Array.from(document.querySelectorAll<HTMLButtonElement>(".slot-live-btn"))) {
    btn.onclick = () => {
      const id = btn.dataset.slot as PhotoSlotId;
      startLive(id);
    };
  }

  for (const btn of Array.from(document.querySelectorAll<HTMLButtonElement>(".slot-use-anyway-btn"))) {
    btn.onclick = () => {
      const id = btn.dataset.slot as PhotoSlotId;
      getSlot(id).forcedOk = true;
      render();
    };
  }

  for (const img of Array.from(document.querySelectorAll<HTMLImageElement>(".slot-example[data-fallback]"))) {
    img.onerror = () => {
      img.onerror = null;
      const fallback = document.createElement("div");
      fallback.className = "slot-example slot-example-fallback";
      fallback.textContent = t("slot_example_pending");
      img.replaceWith(fallback);
    };
  }

  const continueBtn = document.getElementById("slotsContinueBtn") as HTMLButtonElement | null;
  if (continueBtn) {
    continueBtn.onclick = () => {
      if (!allSlotsReady()) return;
      notifyParentOnce("nexwell:intraoral-photos-ready");
      screen = "uploading";
      render();
    };
  }
}

/** Slot bazında yükleme durumu — uploadingScreen render'ında kullanılır. */
const uploadProgress = new Map<PhotoSlotId, UploadProgressStatus>();
let uploadStarted = false;
let uploadFailedAll = false;

function uploadProgressLabel(status: UploadProgressStatus | undefined): string {
  if (status === "uploaded") return `✓ ${t("uploading_progress")}`;
  if (status === "failed") return t("uploading_failed");
  if (status === "uploading") return "…";
  return "";
}

function uploadingScreen(): string {
  const rows = PHOTO_STEPS.map((step) => {
    const status = uploadProgress.get(step.id);
    const cls = status === "uploaded" ? "ok" : status === "failed" ? "err" : "";
    return `<p class="slot-status ${cls}">${t(step.titleKey)}: ${uploadProgressLabel(status) || "…"}</p>`;
  }).join("");
  const anyFailed = Array.from(uploadProgress.values()).some((s) => s === "failed");

  return `
    <div class="screen center">
      <div class="card">
        <h2>${t("uploading_title")}</h2>
        ${rows}
        ${uploadFailedAll ? `<p class="slot-status err">${t("uploading_error_all")}</p>` : ""}
        ${anyFailed || uploadFailedAll ? `<button class="btn" id="uploadRetryBtn">${t("uploading_retry")}</button>` : ""}
      </div>
    </div>`;
}

async function startUpload() {
  if (uploadStarted) return;
  uploadStarted = true;
  uploadFailedAll = false;
  for (const step of PHOTO_STEPS) uploadProgress.set(step.id, "pending");
  render();

  const container = document.getElementById("turnstileContainer") ?? app;
  try {
    const result = await uploadAllSlots(container, (p: UploadProgress) => {
      uploadProgress.set(p.slotId, p.status);
      render();
    });
    if (result.ok) {
      screen = "done";
      render();
    } else if (result.leadId === null) {
      // Session/Turnstile aşamasında başarısız — hiçbir slot denenemedi.
      uploadFailedAll = true;
      uploadStarted = false;
      render();
    } else {
      // Session başarılıydı ama en az bir slot PUT'u başarısız oldu.
      // uploadFailedAll BURADA DA set edilmeli (revizyoncu P0, 2026-09-04,
      // 2. tur) — önceki hali bu dalda bayrağı hiç set etmiyordu, bu da
      // wireUploadingScreen()'in "!uploadStarted && !uploadFailedAll" kapısını
      // yanlışlıkla açık bırakıp kullanıcı Retry'a basmadan startUpload()'ı
      // sessizce yeniden tetikliyordu — her turda YENİ bir lead_id/Turnstile
      // session'ı, sonsuz döngü. Üç terminal dalın (bu, session-fail, catch)
      // ÜÇÜ DE aynı bayrağı set etmeli; otomatik yeniden başlatma yalnız
      // "hiç denenmedi" durumunda olmalı.
      uploadFailedAll = true;
      uploadStarted = false;
      render();
    }
  } catch {
    uploadFailedAll = true;
    uploadStarted = false;
    render();
  }
}

function wireUploadingScreen() {
  const retryBtn = document.getElementById("uploadRetryBtn") as HTMLButtonElement | null;
  if (retryBtn) {
    retryBtn.onclick = () => {
      void startUpload();
    };
  }
  // uploadFailedAll kontrolü KRİTİK (2026-09-04, görsel QA'de bulunan gerçek
  // sonsuz döngü — ADIM 3 review notu): startUpload() bir hatadan sonra
  // uploadStarted'ı false'a döndürüyor ki "tekrar dene" butonu çalışsın.
  // Ama bu fonksiyon HER render()'da (screen "uploading" kaldığı sürece)
  // yeniden çağrılıyor — uploadFailedAll kontrolü olmadan, her başarısız
  // denemeden hemen sonra kullanıcı "tekrar dene"ye basmadan otomatik
  // olarak yeniden başlıyordu: sessiz, sonsuz bir retry döngüsü (Turnstile
  // yanlış yapılandırılmışsa saniyede onlarca deneme). Artık yalnız İLK
  // gösterimde otomatik başlar; bir hatadan sonra yeniden denemek kullanıcı
  // eylemi (retry butonu) gerektirir.
  if (!uploadStarted && !uploadFailedAll) void startUpload();
}

function doneScreen(): string {
  return `
    <div class="screen center">
      <div class="card">
        <p>${t("uploading_done")}</p>
      </div>
    </div>`;
}

function render() {
  const body =
    screen === "consent"
      ? consentScreen()
      : screen === "slots"
        ? slotsScreen()
        : screen === "uploading"
          ? uploadingScreen()
          : doneScreen();

  app.innerHTML = `
    ${topbar()}
    ${body}
    <div id="turnstileContainer" style="position:absolute;width:1px;height:1px;overflow:hidden"></div>
    ${legal()}`;
  wireLang();
  if (screen === "consent") wireConsentScreen();
  if (screen === "slots") wireSlotsScreen();
  if (screen === "uploading") wireUploadingScreen();
  if (liveSlot) wireLiveOverlay();

  // Emniyet (a): canlı oturum sırasında dil değiştirmek video'yu yok eder.
  const langSel = document.getElementById("langSel") as HTMLSelectElement | null;
  if (langSel) langSel.disabled = liveSlot !== null;

  // Emniyet (b): canlı görünüm artık TAM EKRAN değil, ilgili slot kartının
  // İÇİNDE gömülü (Onur'un gerçek cihaz testinde istediği değişiklik) — bu
  // yüzden `.screen`'in tamamını inert yapamayız, aksi halde canlı kartın
  // kendi İptal/Kamera değiştir/Dosyadan seç butonları da devre dışı kalır.
  // Yalnız DİĞER kartlar ve devam butonu inert edilir.
  for (const card of Array.from(app.querySelectorAll<HTMLElement>(".slot-card"))) {
    card.toggleAttribute("inert", liveSlot !== null && card.dataset.slot !== liveSlot);
  }
  document.getElementById("slotsContinueBtn")?.toggleAttribute("inert", liveSlot !== null);

  // Emniyet (c): akış BİR KEZ bağlandıktan SONRA koparsa (render() video'yu
  // yok ettiyse) bilinen-iyi duruma dön. liveStreamAttached ZORUNLU — onsuz
  // kontrol ilk açılış penceresinde (startLiveCapture henüz çağrılmadan,
  // srcObject doğal olarak null) canlı oturumu daha başlamadan kapatır.
  if (
    liveSlot !== null &&
    liveStreamAttached &&
    !(document.getElementById("liveVideo") as HTMLVideoElement | null)?.srcObject
  ) {
    queueMicrotask(() => closeLive());
  }
}

// Başlangıç dili: ?lang=<kod> (sayfa diline göre embed); yoksa varsayılan.
const _params = new URLSearchParams(location.search);
const _qlang = _params.get("lang");
setLang(LANGS.some((l) => l.code === _qlang) ? (_qlang as Lang) : getLang());

render();
notifyParentOnce("nexwell:intraoral-tool-loaded");
